/**
 * DiffViewer component for displaying differences between essay versions.
 * 
 * Features:
 * - Word-level diff visualization
 * - Green highlighting for additions
 * - Red highlighting for deletions
 * - Statistics summary (additions, deletions, word count change)
 * - Toggle between unified and side-by-side view (future)
 * 
 * Called by:
 * - VersionHistory.tsx via modal
 */
import { Typography, Space, Tag, Spin, Empty, Card, Statistic, Row, Col } from 'antd';
import { PlusOutlined, MinusOutlined, SwapOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import type { DiffSegment } from '@compendium/shared';
import { getVersionDiff } from '../services/api';

const { Text } = Typography;

// =============================================================================
// Styling
// =============================================================================

/**
 * Styles for diff segments.
 */
const SEGMENT_STYLES: Record<DiffSegment['type'], React.CSSProperties> = {
  unchanged: {
    color: '#d9d9d9',
  },
  added: {
    backgroundColor: 'rgba(82, 196, 26, 0.3)',
    color: '#95de64',
    textDecoration: 'none',
    padding: '1px 2px',
    borderRadius: '2px',
  },
  removed: {
    backgroundColor: 'rgba(255, 77, 79, 0.3)',
    color: '#ff7875',
    textDecoration: 'line-through',
    padding: '1px 2px',
    borderRadius: '2px',
  },
};

// =============================================================================
// Component Props
// =============================================================================

interface DiffViewerProps {
  /** Essay slug for API call */
  slug: string;
  /** Version ID to compare (compares against previous version) */
  versionId: number;
}

// =============================================================================
// Component
// =============================================================================

/**
 * DiffViewer component.
 * Displays word-level diff between a version and its previous version.
 */
function DiffViewer({ slug, versionId }: DiffViewerProps) {
  // Fetch diff data
  const {
    data: diffResult,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['diff', slug, versionId],
    queryFn: () => getVersionDiff(slug, versionId),
  });

  // Loading state
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '32px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Empty
        description={error instanceof Error ? error.message : 'Failed to load diff'}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  // No diff data
  if (!diffResult) {
    return (
      <Empty
        description="No diff data available"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  // First version (no previous to compare)
  if (diffResult.previousVersion === 0) {
    return (
      <div>
        <Card
          size="small"
          style={{ marginBottom: '16px', background: '#1a1a1a' }}
        >
          <Text type="secondary">
            This is the first version. No previous version to compare against.
          </Text>
        </Card>
        <div
          style={{
            background: '#1a1a1a',
            padding: '16px',
            borderRadius: '8px',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            fontSize: '13px',
            lineHeight: 1.6,
          }}
        >
          {diffResult.diff.map((segment, index) => (
            <span key={index} style={SEGMENT_STYLES[segment.type]}>
              {segment.value}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Statistics */}
      <Card
        size="small"
        style={{ marginBottom: '16px', background: '#1a1a1a' }}
      >
        <Row gutter={16}>
          <Col span={6}>
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Comparing
              </Text>
              <Space>
                <Tag>v{diffResult.previousVersion}</Tag>
                <SwapOutlined />
                <Tag color="gold">v{diffResult.currentVersion}</Tag>
              </Space>
            </Space>
          </Col>
          <Col span={6}>
            <Statistic
              title={<Text type="secondary" style={{ fontSize: '12px' }}>Additions</Text>}
              value={diffResult.stats.additions}
              valueStyle={{ color: '#52c41a', fontSize: '18px' }}
              prefix={<PlusOutlined />}
              suffix="words"
            />
          </Col>
          <Col span={6}>
            <Statistic
              title={<Text type="secondary" style={{ fontSize: '12px' }}>Deletions</Text>}
              value={diffResult.stats.deletions}
              valueStyle={{ color: '#ff4d4f', fontSize: '18px' }}
              prefix={<MinusOutlined />}
              suffix="words"
            />
          </Col>
          <Col span={6}>
            <Statistic
              title={<Text type="secondary" style={{ fontSize: '12px' }}>Net Change</Text>}
              value={diffResult.stats.wordCountChange}
              valueStyle={{
                color: diffResult.stats.wordCountChange >= 0 ? '#52c41a' : '#ff4d4f',
                fontSize: '18px',
              }}
              prefix={diffResult.stats.wordCountChange >= 0 ? <PlusOutlined /> : <MinusOutlined />}
              suffix="words"
            />
          </Col>
        </Row>
      </Card>

      {/* Legend */}
      <Space style={{ marginBottom: '12px' }}>
        <Space size={4}>
          <span
            style={{
              display: 'inline-block',
              width: '12px',
              height: '12px',
              backgroundColor: 'rgba(82, 196, 26, 0.3)',
              borderRadius: '2px',
            }}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Added
          </Text>
        </Space>
        <Space size={4}>
          <span
            style={{
              display: 'inline-block',
              width: '12px',
              height: '12px',
              backgroundColor: 'rgba(255, 77, 79, 0.3)',
              borderRadius: '2px',
            }}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Removed
          </Text>
        </Space>
      </Space>

      {/* Diff content */}
      <div
        style={{
          background: '#1a1a1a',
          padding: '16px',
          borderRadius: '8px',
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
          fontSize: '13px',
          lineHeight: 1.8,
          maxHeight: '50vh',
          overflow: 'auto',
        }}
      >
        {diffResult.diff.map((segment, index) => (
          <span key={index} style={SEGMENT_STYLES[segment.type]}>
            {segment.value}
          </span>
        ))}
      </div>
    </div>
  );
}

export default DiffViewer;
